import { Col, Row } from "@/common/css";
import Icon from "@/common/ui/Image";
import { IconText, LightText, LightText2, LightTextnormal } from "@/common/ui/Text";
import { Separator } from "@/components/ui/separator";
import React from "react";

type POSITION = "justify-start" | "justify-center" | "justify-end";

type FOOTER_CONTENT_POSITION = "top-corner" | "bottom-center" | "bottom-justify-between";

interface TemplateConfig {
  headerStyle: {
    height: string;
    backgroundColor: string;
    logoPosition: POSITION;
    logoSize: string;
    additionalClass: string;
    contentPosition?: FOOTER_CONTENT_POSITION;
  };
  contentStyle: {
    backgroundColor?: string;
  };
  footerStyle: {
    height: string;
    backgroundColor: string;
    additionalClass: string;
    position: FOOTER_CONTENT_POSITION;
  };
}

interface TemplatePreviewProps {
  cmpName: string;
  address: string;
  email: string;
  phone: string;
  templateOptions: any[];
  templateId: number;
  logo: string;
  isViewMode?: boolean;
  isActive?: boolean;
  setValueRef?: any;
  sealAndSignature?: any;
}

interface FooterContentProps {
  position: FOOTER_CONTENT_POSITION;
  cmpName: string;
  address: string;
  phone: string;
  email: string;
}

const FooterContent: React.FC<FooterContentProps> = ({ position, cmpName, address, phone, email }) => {  
  switch (position) {
    case "bottom-center":
      return (
        <div className="flex flex-col items-center text-13">
          <div className="flex flex-col items-center mt-2">
            <LightTextnormal text={cmpName} className="font-[1000] !text-[8px] !text-letterHead" />
            <LightText2 text={address} className="!text-letterHead !leading-[9px]" />
            <Row className="gap-2 !py-0 !gap-y-0">
              {phone && <IconText iconSrc="/icons/phone-gray-icon.svg" text={phone} iconClassName="size-4" textClassName="text-letterHead" />}{" "}
              <IconText iconSrc="/icons/email-gray-icon.svg" text={email} iconClassName="size-4" textClassName="text-letterHead" />
            </Row>
          </div>
        </div>
      );

    case "bottom-justify-between":
      return (
        <div className="flex justify-between items-center w-full ">
          <LightTextnormal text={cmpName} className="font-[1000] !text-letterHead" />
          <LightText2 text={address} className="!text-letterHead" />
          <Col className=" !gap-0.5">
            <LightText2 text={email} className="!text-letterHead" />
            <LightText2 text={phone} className="!text-letterHead" />
          </Col>
        </div>
      );

    case "top-corner":
      return (
        <div className="flex flex-col gap-1 text-13">
          <LightTextnormal text={cmpName} className="font-[1000] !text-letterHead text-end" />
          <LightText2 text={address} className="!text-letterHead text-end" />
          <Row className="gap-2 py-0 gap-y-0">
            {phone && <IconText iconSrc="/icons/phone-gray-icon.svg" text={phone} iconClassName="size-4" textClassName="text-letterHead" />}{" "}
            <IconText iconSrc="/icons/email-gray-icon.svg" text={email} iconClassName="size-4" textClassName="text-letterHead" />
          </Row>
        </div>
      );

    default:
      return <small>Invalid Position</small>;
  }
};

const ActiveTemplate = ({ templateInfo, isActive }: any) => {
  return (
    <Row className="justify-between !p-0 m-4">
      <LightText text={templateInfo?.key} className=" !text-black" />
      {isActive && <span className=" bg-primaryOpacity-10 text-text-primary py-0.5 px-1 !leading-normal text-13">Active</span>}{" "}
    </Row>
  );
};

const TemplatePreview: React.FC<TemplatePreviewProps> = ({
  cmpName,
  address,
  email,
  phone,
  templateOptions,
  templateId,
  logo,
  sealAndSignature = null,
  isViewMode = false,
  isActive = false,
  setValueRef,
}) => {
  const getSelectedTemplate = () => {
    try {
      if (!templateId) return null;
      const selectedTemplate = templateOptions.find((template) => template.id === templateId);
      if (!selectedTemplate) {
        console.warn(`Template with ID ${templateId} not found`);
        return null;
      }
      return selectedTemplate.config;
    } catch (error) {
      console.error("Error finding template:", error);
      return null;
    }
  };
  const selectedTemplate: TemplateConfig = getSelectedTemplate();
  if (!selectedTemplate) {
    return (
      <div className="w-2/5 h-full flex items-center justify-center">
        <p className="text-gray-500">Please select a template</p>
      </div>
    );
  }

  const renderHeader = () => {
    const { backgroundColor, height, logoSize, logoPosition, contentPosition, additionalClass } = selectedTemplate.headerStyle;
    return (
      <div>
        <div className={`flex items-center px-2.5 ${backgroundColor} ${logoPosition} ${additionalClass}`} style={{ height }}>
          <img src={logo} alt="Company Logo" className=" h-12 max-w-20  object-contain" style={{ maxHeight: logoSize, backgroundColor: "transparent" }} />
          {contentPosition && <FooterContent position={contentPosition} cmpName={cmpName} address={address} email={email} phone={phone} />}
        </div>
        <Separator orientation="horizontal" className="flex w-full" />
      </div>
    );
  };

  const renderFooter = () => {
    const { height, position } = selectedTemplate.footerStyle;
    if (!position.includes("bottom")) return null;
    return (
      <div className="w-full">
        <Separator orientation="horizontal" className="flex w-full" />
        <div className={`flex flex-col justify-center p-4 px-4 bg-white text-13`} style={{ height }}>
          <FooterContent position={position} cmpName={cmpName} address={address} email={email} phone={phone} />
        </div>
      </div>
    );
  };

  return (
    <div className={`bg-stroke-light h-[calc(100%-32px)] min-h-[600px] ${isViewMode ? "pb-[38px]" : ""}  w-full md:w-[calc(100%-32px)] md:m-4 rounded-llg`}>
      {isViewMode && <ActiveTemplate templateInfo={templateOptions.find((template) => template.id === templateId)} isActive={isActive} />}
      <div className="bg-white h-[calc(100%-84px)] w-[calc(100%-32px)] m-4 rounded-md ">
        <Col className="justify-between h-full relative">
          {renderHeader()}
          {renderFooter()}
          {sealAndSignature && (
            <div className="absolute left-10 flex items-center gap-1 p-0.5 px-1 rounded" style={{ bottom: "124px" }}>
              <Icon src={sealAndSignature} alt="" className="size-16" />
            </div>
          )}
          <div
            className="absolute right-4 flex items-center gap-1 bg-stroke-light p-0.5 px-1 rounded -translate-y-3"
            style={{ bottom: selectedTemplate.footerStyle.height }}
          >
            <small className="!text-letterHead leading-normal font-normal">Generated with</small>
            <Icon src="/icons/logo.svg" alt="" className="h-3" />
          </div>
        </Col>
        {setValueRef && templateOptions.length > 1 && (
          <TemplateSwitcher
            label={templateOptions.find((template) => template.id === templateId)?.key || ""}
            onPrev={() => {
              const currentIndex = templateOptions.findIndex((t) => t.id === templateId);
              const prevIndex = (currentIndex - 1 + templateOptions.length) % templateOptions.length;
              const value = templateOptions[prevIndex].id;

              // console.log({ currentIndex, prevIndex, value, setValueRef });
              if (value) {
                setValueRef("templateId", templateOptions[prevIndex].id);
              }
            }}
            onNext={() => {
              const currentIndex = templateOptions.findIndex((t) => t.id === templateId);
              const nextIndex = (currentIndex + 1) % templateOptions.length;
              const value = templateOptions[currentIndex].id;
              if (value) {
                setValueRef("templateId", templateOptions[nextIndex].id);
              }
            }}
          />
        )}
      </div>
    </div>
  );
};

export default TemplatePreview;

type Props = {
  label: string;
  onPrev: () => void;
  onNext: () => void;
};

const TemplateSwitcher: React.FC<Props> = ({ label, onPrev, onNext }) => (
  <div
    className="mt-4"
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#f7f7f7",
      padding: "4px 0",
    }}
  >
    <button
      onClick={onPrev}
      style={{
        border: "none",
        background: "#e5e5e5",
        borderRadius: "50%",
        width: "20px",
        height: "20px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
      }}
      aria-label="Previous"
    >
      <Icon src={"/icons/prev_arrow.svg"} alt="" className="size-6" />
    </button>
    <span className="text-13 mx-5">{label}</span>
    <button
      onClick={onNext}
      style={{
        border: "none",
        background: "#e5e5e5",
        borderRadius: "50%",
        width: "20px",
        height: "20px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
      }}
      aria-label="Next"
    >
      <Icon src={"/icons/prev_arrow.svg"} alt="" className="size-6 rotate-180" />
    </button>
  </div>
);
